import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@plannel/components/Dialog";
import { ModalRule } from "@plannel/components/modal/BaseConfigModal";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LaunchIcon from "@mui/icons-material/Launch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ShareIcon from "@mui/icons-material/Share";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import SidePanel, {
  PANEL,
  PANEL_HEAD_ICON_SX,
  PanelBadge,
} from "@plannel/components/ui/SidePanel";
import AppAutocomplete from "@plannel/components/ui/AppAutocomplete";
import p13nService from "@plannel/services/system/p13n-service";
import userService from "@plannel/services/system/user-service";
import usePageLinkOptions, { openMenuTab } from "@plannel/hooks/usePageLinkOptions";
import workflowService from "@plannel/services/system/workflow-service";
import debug from "@plannel/utils/debug-util";
import { getWorkflowPanelOpen, setWorkflowPanelOpen, getWorkflowPanelFocusId } from "@plannel/redux/modules/WorkflowPanelState";
import { FS } from "@plannel/utils/font-util";

// 개인 워크플로우 데이터의 z_p13n 저장 키 (사용자별 1행: UNIQUE(user_id, page_cd)).
//   configObj = { workflows: [{ id, name, steps: [{ id, text, menuKey, done }] }] }
//   menuKey = TabMenuList lv3 메뉴의 key — 페이지 링크 식별자(plannel:openMenu로 탭 열기).
const PAGE_CD = "MY_WORKFLOW";

/*
 * 공유 사본이 사는 곳 — 소유자마다 한 행(user_id=소유자, page_cd=MY_WORKFLOW_SHARED)이다.
 *   configObj = { workflows: [{ id, name, shareScope, postnCds, steps: [{ id, text, menuKey }] }] }
 * 내 목록(MY_WORKFLOW)에서 share 가 붙은 것만 추려 **파생**시킨다(saveWorkflows 참고) —
 * 원본을 고치면 공유분도 같이 따라가므로 받는 쪽은 사본이 아니라 원본을 구독하는 셈이 된다.
 * 진행률(done)은 여기 넣지 않는다. 체크는 보는 사람마다 다르고, 각자 자기 MY_WORKFLOW 행의
 * sharedProgress 에 쌓인다.
 * 읽기는 GET /api/workflow/shared 로만 한다 — 공유 범위 판정은 서버 몫이다(WorkflowShareController).
 */
const SHARED_PAGE_CD = "MY_WORKFLOW_SHARED";
const SHARE_SCOPE_ALL = "ALL";
const SHARE_SCOPE_POSTN = "POSTN";
/*
 * 스피너를 내기 전에 기다리는 시간. 캐시가 더워 응답이 즉시 오는 경우가 흔한데,
 * 그때 스피너를 바로 걸면 한 프레임 나타났다 사라져 오히려 깜빡임으로 읽힌다.
 * 이 시간 안에 끝나면 스피너 없이 목록이 바로 붙는다.
 */
const SPINNER_DELAY_MS = 200;
// 패널 너비: 좌측 가장자리 드래그로 조절, localStorage 기억 (AI Agent 패널과 동일 패턴)
const WIDTH_STORAGE_KEY = "plannel_workflow_panel_width";
const MIN_WIDTH = 280;
const MAX_WIDTH = 680;
const DEFAULT_WIDTH = 340;

/*
 * 펼친 카드의 단계 목록 높이 상한 — 넘으면 카드 **안에서** 스크롤한다.
 * 상한이 없으면 단계가 많은 워크플로우 하나가 패널을 다 차지해, 다른 워크플로우를 보려면
 * 그 카드를 지나칠 때까지 패널을 한참 굴려야 한다. 카드마다 자기 목록을 굴리게 두면
 * 여러 워크플로우가 한 화면에 남는다.
 * 값은 페이지 링크 칩이 붙은 단계 기준 5개쯤(칩 없으면 8개쯤) 보이는 높이다.
 */
const STEP_LIST_MAX_HEIGHT = 288;

/*
 * 사용자가 카드 아래 손잡이를 끌어 목록 높이를 직접 정할 수 있다.
 * **저장하지 않는다** — 화면에서 잠깐 넓혀 보는 용도라 컴포넌트 state 에만 둔다.
 * 패널을 닫았다 열거나 새로고침하면 기본값으로 돌아간다(패널 폭과 달리 localStorage·p13n 어디에도 안 쓴다).
 *
 * 한 번이라도 끌었으면 위 상한(maxHeight)을 걷고 **끈 높이(height)를 그대로** 쓴다.
 * 상한으로만 두면 내용이 상한보다 짧은 카드(단계 2개짜리)는 값을 바꿔도 화면이 그대로라
 * 손잡이가 고장 난 것처럼 보인다 — 짧은 목록도 끌리는 대로 늘고 줄어야 한다.
 * 최소는 단계 한 줄은 남는 높이, 최대는 패널을 혼자 다 먹지 않는 선.
 */
const STEP_LIST_MIN_HEIGHT = 48;
const STEP_LIST_DRAG_MAX_HEIGHT = 640;

/*
 * 패널 안 입력칸 — NotesModal 의 작성 카드와 같은 문법이다. 테두리(노치) 대신 inset 링을 쓰고
 * 포커스에서 링만 청록으로 켠다. outlined 기본 테두리는 세 겹으로 겹쳐 보여서 전부 꺼야 한다.
 * (호출부에서 `inputProps.style` 로 크기를 주지 말 것 — 인라인 style 이 이 sx 를 이긴다.)
 */
const PANEL_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
    boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
    transition: "box-shadow .15s ease",
    "& .MuiOutlinedInput-notchedOutline": { border: 0 },
    "&:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
    "&.Mui-focused": { boxShadow: `inset 0 0 0 2px ${PANEL.accentLineHover}` },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 11px",
    fontSize: FS.TITLE,
    color: PANEL.ink,
    "&::placeholder": { color: PANEL.inkFaint, opacity: 1 },
  },
};

/*
 * 행 안의 작은 아이콘 버튼(수정·삭제·초기화·이동). 헤더 아이콘(PANEL_HEAD_ICON_SX)보다 한 치수 작다.
 * 색은 idle → hover 로 바뀌기만 하고 배경이 생기지 않는다 — 패널 전체가 그 문법이다.
 */
const PANEL_ROW_ICON_SX = {
  p: "3px",
  color: PANEL.iconIdle,
  "& > svg": { fontSize: 15 },
  "&:hover": { color: PANEL.accentDeep, bgcolor: "transparent" },
};

/* 확인 액션(단계 추가·수정 저장) — 앱 표준 버튼 색을 쓴다. 청록(PANEL.accent)은 상태 표시 전용이다. */
const PANEL_CONFIRM_ICON_SX = {
  p: "5px",
  bgcolor: "primary.main",
  color: "primary.contrastText",
  "& > svg": { fontSize: 15 },
  "&:hover": { bgcolor: "primary.dark" },
  "&.Mui-disabled": { bgcolor: PANEL.fieldBg, color: PANEL.iconFaint },
};

// 페이지 선택 드롭다운 — 공용 AppAutocomplete(그룹 헤더 아이콘·옵션 아이콘·하이라이트 내장) 사용.
const PageAutocomplete = ({ options, groupIcons, value, onChange, placeholder }) => (
  <AppAutocomplete options={options} groupIcons={groupIcons} placeholder={placeholder}
    value={value} onChange={onChange}
    sx={{
      flex: 1,
      minWidth: 0,
      "& .MuiInputBase-root": {
        bgcolor: "#fff",
        borderRadius: "10px",
        boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
        "&:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
        "&.Mui-focused": { boxShadow: `inset 0 0 0 2px ${PANEL.accentLineHover}` },
      },
      "& .MuiInputBase-input": { fontSize: FS.BODY, color: PANEL.ink },
    }} />
);

/*
 * 공유 사본으로 내보낼 모양 — 단계 정의만 남기고 진행률(done)은 뺀다.
 * 공유 설정(share)은 내 목록에만 두고, 사본에는 서버가 범위를 판정할 값(shareScope·postnCds)만 펼쳐 넣는다.
 */
const toSharedWorkflow = (wf) => ({
  id: wf.id,
  name: wf.name,
  shareScope: wf.share.scope,
  postnCds: wf.share.scope === SHARE_SCOPE_POSTN ? (wf.share.postnCds ?? []) : [],
  steps: wf.steps.map((step) => ({ id: step.id, text: step.text, menuKey: step.menuKey ?? null })),
});

const buildSharedPayload = (list) => list.filter((wf) => wf.share).map(toSharedWorkflow);

/*
 * 공유받은 워크플로우의 진행률 키. 워크플로우 id 는 만든 시각(Date.now())이라 사람이 다르면 겹칠 수 있어,
 * 소유자까지 묶어야 남의 워크플로우 두 개가 같은 체크 상태를 나눠 갖는 일이 없다.
 */
const sharedKey = (ownerId, workflowId) => `${ownerId}:${workflowId}`;

// id 기준 배열 순서 이동 — dragged 항목을 target 항목 위치로 옮긴다.
const moveById = (list, fromId, toId) => {
  const fromIdx = list.findIndex((item) => item.id === fromId);
  const toIdx = list.findIndex((item) => item.id === toId);
  if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return list;
  const next = [...list];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
};

const WorkflowPanelBody = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [workflows, setWorkflows] = useState([]);
  /*
   * 목록을 채우는 두 호출(내 워크플로우 + 공유받은 것)이 끝나기 전까지 참.
   * 이게 없으면 패널을 열자마자 workflows 가 빈 배열이라 "등록된 워크플로우가 없습니다"가
   * 먼저 떴다가 응답이 와서야 목록으로 바뀐다 — 워크플로우가 있는 사용자에게 없다고 말하는 셈이다.
   */
  const [loading, setLoading] = useState(true);
  // loading 이 SPINNER_DELAY_MS 를 넘겼을 때만 참 — 스피너를 실제로 그릴지 가른다.
  const [showSpinner, setShowSpinner] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);          // { id, name } — 워크플로우 이름 수정
  const [editingStep, setEditingStep] = useState(null);  // { wfId, stepId, text, page } — 단계 수정
  const [drafts, setDrafts] = useState({});              // { [workflowId]: { text, page } }
  const [expanded, setExpanded] = useState({});          // { [workflowId]: boolean }

  /*
   * 공유 — 세 갈래로 나뉜다.
   *   sharing        : 공유 설정 모달 상태 { id, name, scope, postnCds } (소유자 쪽)
   *   sharedGroups   : 남이 나에게 공유한 것 [{ ownerId, owner, ownerName, workflows }] (받는 쪽)
   *   sharedProgress : 그 중 내가 체크한 단계 { [`ownerId:wfId`]: { [stepId]: true } } — 내 개인 행에 저장
   */
  const [sharing, setSharing] = useState(null);
  const [sharedGroups, setSharedGroups] = useState([]);
  const [sharedProgress, setSharedProgress] = useState({});
  const [postnOptions, setPostnOptions] = useState([]);
  /*
   * 마지막으로 서버에 써 넣은 공유 사본(JSON). 내 목록이 바뀔 때마다 사본을 다시 만들되,
   * 내용이 같으면 쓰지 않으려고 들고 있는다 — 단계 하나 체크할 때마다 공유 행까지 건드릴 이유가 없다.
   */
  const sharedSnapshotRef = useRef("[]");

  // 홈 '내 할 일' 클릭 등으로 특정 워크플로우를 펼친 채 열기 — focusId 를 소비해 해당 목록을 펼친다.
  const focusId = useSelector(getWorkflowPanelFocusId);
  useEffect(() => {
    if (focusId != null) setExpanded((prev) => ({ ...prev, [focusId]: true }));
  }, [focusId]);

  // 드래그 정렬 상태 — 핸들(mousedown)에서만 draggable을 켜 헤더 클릭/텍스트 선택과 충돌 방지.
  const [dragWfId, setDragWfId] = useState(null);        // 드래그 중인 워크플로우 id
  const [dragOverWfId, setDragOverWfId] = useState(null);
  const [wfDragEnabledId, setWfDragEnabledId] = useState(null);
  const [dragStep, setDragStep] = useState(null);        // { wfId, stepId }
  const [dragOverStepId, setDragOverStepId] = useState(null);
  const [stepDragEnabledId, setStepDragEnabledId] = useState(null);

  const [panelWidth, setPanelWidth] = useState(() => {
    try { const v = Number(localStorage.getItem(WIDTH_STORAGE_KEY)); return v >= MIN_WIDTH && v <= MAX_WIDTH ? v : DEFAULT_WIDTH; }
    catch { return DEFAULT_WIDTH; }
  });
  useEffect(() => {
    try { localStorage.setItem(WIDTH_STORAGE_KEY, String(panelWidth)); } catch { /* ignore */ }
  }, [panelWidth]);
  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev) => {
      // 왼쪽으로 끌면 넓어짐 (패널이 화면 우측에 있으므로)
      setPanelWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + (startX - ev.clientX))));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }, [panelWidth]);

  /*
   * 카드별 단계 목록 높이 — 워크플로우 id 로 기억한다. 일부러 **저장하지 않는다**(위 상수 주석 참고).
   * 값이 없는 카드는 기본 상한(STEP_LIST_MAX_HEIGHT)을 쓴다.
   */
  const [stepListHeights, setStepListHeights] = useState({});
  // 드래그 시작 높이를 재려고 목록 노드를 워크플로우 id 로 들고 있는다.
  const stepListRefs = useRef({});

  /*
   * 삭제는 **모드**로 가른다.
   * 예전엔 카드 줄 오른쪽 끝에 [초기화][이름변경][삭제] 다음 바로 접기 화살표가 붙어 있었다.
   * 카드를 접으려다 한 칸 왼쪽을 누르면 그대로 삭제였고, 확인도 되돌리기도 없어 단계 전부가 사라졌다.
   * 그래서 삭제를 카드 줄에서 아예 빼내 패널 헤더(워크플로우 추가 옆)로 올린다 —
   * 평소에는 카드 어디를 눌러도 삭제될 일이 없고, 지우려면 먼저 "지우겠다"고 말해야 한다.
   */
  const [deleteMode, setDeleteMode] = useState(false);
  // 삭제 확인 대상(모달). 되돌릴 수 없는 동작이라 모드 안에서도 한 번 더 묻는다.
  const [confirmDeleteWf, setConfirmDeleteWf] = useState(null);

  const startStepListResize = useCallback((workflowId) => (e) => {
    // 손잡이는 카드 헤더 밖이지만, 드래그가 카드의 펼침 토글로 새어 나가지 않게 막는다.
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    /*
     * 시작 높이는 상수가 아니라 **지금 그려진 높이**로 잡는다.
     * 아직 안 끈 카드는 maxHeight 만 걸려 있어 실제 높이가 그보다 짧을 수 있는데(단계 2개짜리),
     * 상한값에서 출발하면 첫 드래그에 목록이 그 높이로 튄다.
     */
    const listEl = stepListRefs.current[workflowId];
    const startH = listEl ? Math.round(listEl.getBoundingClientRect().height) : STEP_LIST_MAX_HEIGHT;
    const onMove = (ev) => {
      const next = Math.min(STEP_LIST_DRAG_MAX_HEIGHT, Math.max(STEP_LIST_MIN_HEIGHT, startH + (ev.clientY - startY)));
      setStepListHeights((prev) => ({ ...prev, [workflowId]: next }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "row-resize";
  }, []);

  useEffect(() => {
    const myWorkflowsReq = p13nService.get(PAGE_CD)
      .then((response) => {
        const list = response.data?.workflows ?? [];
        setWorkflows(list);
        setSharedProgress(response.data?.sharedProgress ?? {});
        // 처음 읽은 값을 기준선으로 잡는다 — 패널을 열기만 해도 같은 사본을 다시 쓰지 않게.
        sharedSnapshotRef.current = JSON.stringify(buildSharedPayload(list));
      })
      .catch(e => { debug(e); });

    const sharedReq = workflowService.getShared()
      .then((response) => setSharedGroups(response.data ?? []))
      .catch(e => { debug(e); });

    /*
     * 본문에 나올 것을 채우는 두 호출이 **모두** 끝나야 로딩을 내린다. 하나만 보고 내리면
     * 나머지가 늦게 올 때 빈 화면이 잠깐 스쳤다가 목록이 붙는, 지금 고치려는 그 깜빡임이 남는다.
     * 위 두 체인은 각자 catch 로 닫혀 있어 실패해도 reject 되지 않는다 —
     * 응답이 없어도 로딩에 갇히지 않고 빈 화면까지는 간다.
     * (userAccess·postnOptions 는 페이지 선택지와 공유 대화상자에서만 쓰여 목록 표시를 막지 않으므로 세지 않는다.)
     */
    // 스피너는 곧바로 걸지 않는다 — SPINNER_DELAY_MS 참고.
    const spinnerTimer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    Promise.all([myWorkflowsReq, sharedReq]).then(() => {
      // 타이머가 아직 안 터졌으면 여기서 거둔다 — 늦게 터져 다 끝난 화면에 스피너를 얹지 않게.
      clearTimeout(spinnerTimer);
      setLoading(false);
    });

    /*
     * 공유 대상 직책 목록. /api/user/postn 은 이름과 달리 테넌트의 **전 직책**을 돌려준다
     * (UserPostnMapper 주석 참고) — 공유 대상을 고르는 자리에는 그게 맞다.
     */
    userService.getUserPostn()
      .then((response) => setPostnOptions((response.data ?? [])
        .filter((postn) => postn?.postnCd && postn.activeFlg !== false)
        .map((postn) => ({ key: postn.postnCd, label: postn.name ? `${postn.name} (${postn.postnCd})` : postn.postnCd }))))
      .catch(e => { debug(e); });

    // 응답이 오기 전에 패널을 닫으면(언마운트) 남은 타이머를 거둔다.
    return () => clearTimeout(spinnerTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 페이지 선택지와 권한 판정은 공용 훅이 들고 있다(회의 안건도 같은 것을 고른다)
  const { options: pageOptions, groupIcons, pageMap: permittedPageMap } = usePageLinkOptions();

  const saveWorkflows = useCallback((next) => {
    setWorkflows(next);
    p13nService.upsert({ pageCd: PAGE_CD, configObj: { workflows: next } }).catch(e => { debug(e); });

    /*
     * 공유 사본은 내 목록에서 파생된다 — 목록이 바뀌는 자리는 전부 여기를 지나므로 여기 한 곳에서만 맞춘다.
     * 그래야 공유한 워크플로우의 이름·단계를 고치면 받는 쪽에도 그대로 따라간다.
     * 사본 내용이 그대로면(체크만 바뀐 경우 등) 쓰지 않는다.
     */
    const sharedJson = JSON.stringify(buildSharedPayload(next));
    if (sharedJson === sharedSnapshotRef.current) return;
    sharedSnapshotRef.current = sharedJson;
    p13nService.upsert({ pageCd: SHARED_PAGE_CD, configObj: { workflows: JSON.parse(sharedJson) } })
      .catch(e => { debug(e); });
  }, []);

  // 공유 모달 열기 — 이미 공유 중이면 그 설정을 그대로 띄우고, 처음이면 전원 공개를 기본으로 둔다.
  const openShare = (workflow) => setSharing({
    id: workflow.id,
    name: workflow.name,
    scope: workflow.share?.scope ?? SHARE_SCOPE_ALL,
    postnCds: workflow.share?.postnCds ?? [],
  });

  /*
   * 공유 설정 저장. 범위가 직책이면 최소 한 개는 골라야 한다 — 빈 목록은 "아무도 못 보는 공유"라
   * 공유했다고 표시만 되고 실제로는 아무 일도 안 일어난다(모달의 확인 버튼도 그때는 잠가 둔다).
   */
  const handleSaveShare = () => {
    if (sharing == null) return;
    const postnCds = sharing.postnCds ?? [];
    if (sharing.scope === SHARE_SCOPE_POSTN && postnCds.length === 0) return;
    saveWorkflows(workflows.map((wf) => wf.id === sharing.id
      ? { ...wf, share: { scope: sharing.scope, postnCds: sharing.scope === SHARE_SCOPE_POSTN ? postnCds : [] } }
      : wf));
    setSharing(null);
  };

  // 공유 해제 — share 키를 지우면 다음 저장에서 공유 사본 배열에서도 빠진다.
  const handleUnshare = (workflowId) => {
    saveWorkflows(workflows.map((wf) => {
      if (wf.id !== workflowId) return wf;
      const next = { ...wf };
      delete next.share;
      return next;
    }));
    setSharing(null);
  };

  /*
   * 공유받은 워크플로우의 진행률 — **내** MY_WORKFLOW 행에 쌓는다.
   * 서버 upsert 가 최상위 키 단위 병합이라 sharedProgress 만 보내도 workflows 는 그대로 남는다.
   */
  const saveSharedProgress = (next) => {
    setSharedProgress(next);
    p13nService.upsert({ pageCd: PAGE_CD, configObj: { sharedProgress: next } }).catch(e => { debug(e); });
  };

  const handleToggleSharedStep = (ownerId, workflowId, stepId) => {
    const key = sharedKey(ownerId, workflowId);
    const done = { ...(sharedProgress[key] ?? {}) };
    if (done[stepId]) {
      delete done[stepId];
    } else {
      done[stepId] = true;
    }
    saveSharedProgress({ ...sharedProgress, [key]: done });
  };

  const handleResetSharedProgress = (ownerId, workflowId) => {
    const next = { ...sharedProgress };
    delete next[sharedKey(ownerId, workflowId)];
    saveSharedProgress(next);
  };

  const handleAddWorkflow = () => {
    const name = newName.trim();
    if (!name) return;
    const workflow = { id: Date.now(), name, steps: [] };
    saveWorkflows([...workflows, workflow]);
    setExpanded({ ...expanded, [workflow.id]: true });
    setNewName("");
    setAdding(false);
  };

  const handleRenameWorkflow = () => {
    const name = editing?.name?.trim();
    if (!name) return;
    saveWorkflows(workflows.map((wf) => wf.id === editing.id ? { ...wf, name } : wf));
    setEditing(null);
  };

  const handleDeleteWorkflow = (workflowId) => {
    saveWorkflows(workflows.filter((wf) => wf.id !== workflowId));
  };

  const handleToggleFavorite = (workflowId) => {
    saveWorkflows(workflows.map((wf) => wf.id === workflowId ? { ...wf, favorite: !wf.favorite } : wf));
  };

  // 체크한 단계를 모두 해제 — 반복 절차를 처음부터 다시 시작할 때 사용.
  const handleResetWorkflow = (workflowId) => {
    saveWorkflows(workflows.map((wf) => wf.id !== workflowId ? wf
      : { ...wf, steps: wf.steps.map((step) => step.done ? { ...step, done: false } : step) }));
  };

  const handleAddStep = (workflowId) => {
    const draft = drafts[workflowId];
    const text = draft?.text?.trim();
    if (!text) return;
    const step = { id: Date.now(), text, menuKey: draft?.page?.key ?? null, done: false };
    saveWorkflows(workflows.map((wf) => wf.id === workflowId ? { ...wf, steps: [...wf.steps, step] } : wf));
    setDrafts({ ...drafts, [workflowId]: { text: "", page: null } });
  };

  const handleToggleStep = (workflowId, stepId) => {
    saveWorkflows(workflows.map((wf) => wf.id !== workflowId ? wf
      : { ...wf, steps: wf.steps.map((step) => step.id === stepId ? { ...step, done: !step.done } : step) }));
  };

  const handleDeleteStep = (workflowId, stepId) => {
    saveWorkflows(workflows.map((wf) => wf.id !== workflowId ? wf
      : { ...wf, steps: wf.steps.filter((step) => step.id !== stepId) }));
  };

  const handleSaveStepEdit = () => {
    const text = editingStep?.text?.trim();
    if (!text) return;
    saveWorkflows(workflows.map((wf) => wf.id !== editingStep.wfId ? wf
      : {
        ...wf,
        steps: wf.steps.map((step) => step.id === editingStep.stepId
          ? { ...step, text, menuKey: editingStep.page?.key ?? null } : step),
      }));
    setEditingStep(null);
  };

  const handleMoveWorkflow = (fromId, toId) => {
    saveWorkflows(moveById(workflows, fromId, toId));
  };

  // 단계 이동 — 같은 워크플로우 내 재정렬 + 다른 워크플로우로 이동 모두 지원.
  //   toStepId가 null이면 대상 워크플로우 맨 끝에 추가.
  const handleMoveStepTo = (toWfId, toStepId) => {
    const { wfId: fromWfId, stepId } = dragStep;
    const step = workflows.find((wf) => wf.id === fromWfId)?.steps.find((s) => s.id === stepId);
    if (!step) return;
    saveWorkflows(workflows.map((wf) => {
      if (wf.id === fromWfId && wf.id === toWfId) {
        return toStepId != null ? { ...wf, steps: moveById(wf.steps, stepId, toStepId) }
          : { ...wf, steps: [...wf.steps.filter((s) => s.id !== stepId), step] };
      }
      if (wf.id === fromWfId) {
        return { ...wf, steps: wf.steps.filter((s) => s.id !== stepId) };
      }
      if (wf.id === toWfId) {
        const idx = toStepId != null ? wf.steps.findIndex((s) => s.id === toStepId) : -1;
        const next = [...wf.steps];
        next.splice(idx < 0 ? next.length : idx, 0, step);
        return { ...wf, steps: next };
      }
      return wf;
    }));
    // 다른 워크플로우로 옮겼으면 결과가 보이도록 대상 카드를 펼친다
    if (fromWfId !== toWfId) setExpanded((prev) => ({ ...prev, [toWfId]: true }));
  };

  const clearStepDrag = () => {
    setDragStep(null);
    setDragOverStepId(null);
    setDragOverWfId(null);
    setStepDragEnabledId(null);
  };

  const clearWfDrag = () => {
    setDragWfId(null);
    setDragOverWfId(null);
    setWfDragEnabledId(null);
  };

  const StepEditRow = () => (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
      <TextField size="small" autoFocus value={editingStep.text}
        onChange={(e) => setEditingStep({ ...editingStep, text: e.target.value })}
        onKeyDown={(e) => { if (e.key === "Enter") handleSaveStepEdit(); }}
        sx={PANEL_INPUT_SX} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <PageAutocomplete options={pageOptions} groupIcons={groupIcons} placeholder={t("selectPage")}
          value={editingStep.page} onChange={(_e, page) => setEditingStep({ ...editingStep, page })} />
        <Tooltip title={t("save")}>
          <span>
            <IconButton size="small" disabled={!editingStep.text?.trim()} onClick={handleSaveStepEdit}
              sx={PANEL_CONFIRM_ICON_SX}>
              <DoneIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("cancel")}>
          <IconButton size="small" onClick={() => setEditingStep(null)} sx={PANEL_ROW_ICON_SX}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const WorkflowStep = (workflow, step, isLast) => {
    // 저장 후 권한이 회수되거나 메뉴가 삭제된 페이지는 링크를 비활성화한다.
    const page = step.menuKey != null ? permittedPageMap.get(step.menuKey) : null;
    const isStepEditing = editingStep?.wfId === workflow.id && editingStep?.stepId === step.id;
    const isDragOver = dragStep != null && dragOverStepId === step.id && dragStep.stepId !== step.id;

    return (
      <Box key={step.id}
        draggable={stepDragEnabledId === step.id}
        onDragStart={(e) => { e.stopPropagation(); setDragStep({ wfId: workflow.id, stepId: step.id }); }}
        onDragOver={(e) => {
          if (dragStep == null) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOverStepId(step.id);
        }}
        onDrop={(e) => {
          if (dragStep == null) return;
          e.preventDefault();
          e.stopPropagation();
          handleMoveStepTo(workflow.id, step.id);
          clearStepDrag();
        }}
        onDragEnd={clearStepDrag}
        sx={{
          display: "flex", gap: 0.5, "&:hover .step-actions": { opacity: 1 },
          opacity: dragStep?.stepId === step.id ? 0.4 : 1,
          borderTop: 2, borderColor: isDragOver ? PANEL.accent : "transparent",
          transition: "opacity 0.15s",
        }}>
        {/* 이동 핸들 — 행 맨 앞(작업 내용 앞), 호버 시 표시 */}
        <Box className="step-actions" onMouseDown={() => setStepDragEnabledId(step.id)} onMouseUp={() => setStepDragEnabledId(null)}
          sx={{ display: "flex", alignItems: "flex-start", pt: 0.5, width: 16, flexShrink: 0, opacity: 0, transition: "opacity 0.15s", cursor: "grab", "&:active": { cursor: "grabbing" } }}>
          <DragIndicatorIcon sx={{ fontSize: 15, color: PANEL.iconFaint }} />
        </Box>
        {/* 타임라인: 체크 원형 + 연결선 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 24, flexShrink: 0 }}>
          <Checkbox size="small" checked={!!step.done} onChange={() => handleToggleStep(workflow.id, step.id)}
            icon={<RadioButtonUncheckedIcon sx={{ fontSize: 18, color: PANEL.iconFaint }} />}
            checkedIcon={<CheckCircleIcon sx={{ fontSize: 18, color: PANEL.accent }} />}
            sx={{ p: 0.25 }} />
          {!isLast && <Box sx={{ flex: 1, width: "2px", bgcolor: step.done ? PANEL.accentLine : PANEL.line, borderRadius: 1, my: 0.25 }} />}
        </Box>
        {isStepEditing ? (
          <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 1.25 }}>{StepEditRow()}</Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
              {/* Typography 를 쓰지 않는다 — 전역 SCSS 가 .MuiTypography-root 의 font-size 를 덮는다(SidePanel 주석 참고). */}
              <Box sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, lineHeight: 1.5, wordBreak: "break-word", textDecoration: step.done ? "line-through" : "none", color: step.done ? PANEL.inkFaint : PANEL.inkBody }}>
                {step.text}
              </Box>
              <Box className="step-actions" sx={{ display: "flex", opacity: 0, transition: "opacity 0.15s", flexShrink: 0 }}>
                <IconButton size="small" sx={PANEL_ROW_ICON_SX}
                  onClick={() => setEditingStep({ wfId: workflow.id, stepId: step.id, text: step.text, page: page ?? null })}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteStep(workflow.id, step.id)}
                  sx={{ ...PANEL_ROW_ICON_SX, "&:hover": { color: PANEL.danger, bgcolor: "transparent" } }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Box>
            {step.menuKey != null && (
              <Chip size="small" clickable={!!page} disabled={!page}
                icon={<LaunchIcon sx={{ fontSize: "13px !important" }} />}
                label={page?.label ?? "-"}
                onClick={page ? () => openMenuTab(step.menuKey) : undefined}
                sx={{
                  mt: 0.5, height: 22, maxWidth: "100%", fontWeight: 500,
                  bgcolor: PANEL.accentWash,
                  color: PANEL.accentDeep,
                  boxShadow: `inset 0 0 0 1px ${PANEL.accentLine}`,
                  "& .MuiChip-icon": { color: PANEL.accentDeep },
                  "&:hover": { bgcolor: PANEL.accentSoft },
                  "&.Mui-disabled": { opacity: 1, bgcolor: PANEL.fieldBg, color: PANEL.inkFaint, boxShadow: "none", "& .MuiChip-icon": { color: PANEL.iconFaint } },
                }} />
            )}
          </Box>
        )}
      </Box>
    );
  };

  const WorkflowItem = (workflow) => {
    const doneCount = workflow.steps.filter((step) => step.done).length;
    const total = workflow.steps.length;
    const percent = total === 0 ? 0 : Math.round((doneCount / total) * 100);
    const complete = total > 0 && doneCount === total;
    const draft = drafts[workflow.id] ?? { text: "", page: null };
    const isEditing = editing?.id === workflow.id;
    const isExpanded = !!expanded[workflow.id];
    // 사용자가 손잡이로 바꾼 높이(저장 안 함). 안 건드렸으면 기본 상한.
    // 사용자가 손잡이로 정한 높이(저장 안 함). 없으면 아직 안 끈 것 — 그때만 기본 상한을 씌운다.
    const userStepListHeight = stepListHeights[workflow.id];
    // 카드 하이라이트: 워크플로우 재정렬 대상이거나, 다른 워크플로우의 단계를 옮겨오는 대상일 때
    const isDragOver = dragOverWfId === workflow.id
      && (dragWfId != null ? dragWfId !== workflow.id : dragStep != null && dragStep.wfId !== workflow.id);

    return (
      <Box key={workflow.id}
        draggable={wfDragEnabledId === workflow.id}
        onDragStart={() => setDragWfId(workflow.id)}
        onDragOver={(e) => {
          if (dragWfId == null && dragStep == null) return;
          e.preventDefault();
          setDragOverWfId(workflow.id);
        }}
        onDrop={(e) => {
          if (dragWfId != null) {
            e.preventDefault();
            handleMoveWorkflow(dragWfId, workflow.id);
            clearWfDrag();
          } else if (dragStep != null) {
            // 단계를 카드(단계 행 밖 영역)에 드롭 → 이 워크플로우 맨 끝에 추가
            e.preventDefault();
            handleMoveStepTo(workflow.id, null);
            clearStepDrag();
          }
        }}
        onDragEnd={clearWfDrag}
        /*
         * 카드 룩은 PanelRow 와 같은 문법이다 — 테두리는 border 가 아니라 inset box-shadow.
         * (border 를 쓰면 hover 그림자와 겹칠 때 1px 만큼 레이아웃이 흔들린다. SidePanel 주석 참고)
         * 다만 PanelRow 를 그대로 쓰지는 않는다: 이 카드는 펼침·진행바·중첩 단계를 갖는다.
         */
        sx={{
          mb: "9px", borderRadius: "12px",
          bgcolor: isExpanded ? PANEL.accentWash : "#fff",
          boxShadow: `inset 0 0 0 1px ${isDragOver ? PANEL.accent : isExpanded ? PANEL.accentLine : PANEL.line}`,
          overflow: "hidden", transition: "box-shadow .15s ease, background .15s ease",
          opacity: dragWfId === workflow.id ? 0.4 : 1,
          "&:hover": {
            boxShadow: isExpanded
              ? `inset 0 0 0 1px ${PANEL.accentLineHover}, 0 4px 12px rgba(18,163,198,.16)`
              : `inset 0 0 0 1px ${PANEL.accentLine}, 0 4px 12px rgba(11,60,92,.08)`,
          },
          "&:hover .wf-actions": { opacity: 1 },
        }}>
        {/* 카드 헤더 */}
        <Box onClick={() => !isEditing && setExpanded({ ...expanded, [workflow.id]: !isExpanded })}
          sx={{ display: "flex", alignItems: "center", gap: 0.75, px: "13px", pt: "11px", pb: "9px", cursor: "pointer" }}>
          {isEditing ? (
            <>
              <TextField size="small" autoFocus value={editing.name} sx={{ flex: 1, minWidth: 0, ...PANEL_INPUT_SX }}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleRenameWorkflow(); } }} />
              <Tooltip title={t("save")}>
                <IconButton size="small" sx={PANEL_CONFIRM_ICON_SX} onClick={(e) => { e.stopPropagation(); handleRenameWorkflow(); }}>
                  <DoneIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              {/* 즐겨찾기 별 — 제목 줄 맨 왼쪽, 항상 표시 */}
              <Tooltip title={t("favorite")}>
                <IconButton size="small" sx={{ p: 0.25, ml: -0.5, flexShrink: 0 }}
                  onClick={(e) => { e.stopPropagation(); handleToggleFavorite(workflow.id); }}>
                  {workflow.favorite
                    ? <StarIcon sx={{ fontSize: 16, color: PANEL.star }} />
                    : <StarBorderIcon sx={{ fontSize: 16, color: PANEL.iconFaint }} />}
                </IconButton>
              </Tooltip>
              {/* 이동 핸들 — 별 뒤, 호버 시 표시 */}
              <Box className="wf-actions" onClick={(e) => e.stopPropagation()} onMouseDown={() => setWfDragEnabledId(workflow.id)} onMouseUp={() => setWfDragEnabledId(null)}
                sx={{ display: "flex", alignItems: "center", width: 16, flexShrink: 0, opacity: 0, transition: "opacity 0.15s", cursor: "grab", "&:active": { cursor: "grabbing" } }}>
                <DragIndicatorIcon sx={{ fontSize: 15, color: PANEL.iconFaint }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, fontWeight: 500, color: PANEL.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {workflow.name}
              </Box>
              {/* 공유 중이면 아이콘을 **늘** 띄운다 — 호버해야 보이는 자리에 두면 "이게 남에게 보이는 중"임을
                  알 방법이 없다. 눌러서 범위를 바꾸거나 해제하는 입구도 겸한다. */}
              {workflow.share && (
                <Tooltip title={t("shareWorkflow")}>
                  <IconButton size="small" sx={{ ...PANEL_ROW_ICON_SX, color: PANEL.accentDeep, flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); openShare(workflow); }}>
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              )}
              {/* 완료면 청록 배지(패널의 상태 표시 문법), 진행 중이면 중립 알약 */}
              {complete ? (
                <PanelBadge sx={{ lineHeight: 1.5 }}>100%</PanelBadge>
              ) : (
                <Box
                  sx={{
                    flex: "none", fontSize: FS.CAPTION, fontWeight: 500, lineHeight: 1.5,
                    color: PANEL.inkMuted, bgcolor: PANEL.fieldBg, borderRadius: "9px", px: "9px", py: "2px",
                  }}>
                  {`${doneCount}/${total}`}
                </Box>
              )}
              {/* 삭제 모드에서는 초기화·이름변경도 감춘다 — 이 줄에서 누를 수 있는 건 삭제 하나뿐이어야
                  "옆 버튼을 잘못 눌렀다"가 다시 생기지 않는다. */}
              {!deleteMode && (
                <Box className="wf-actions" sx={{ display: "flex", opacity: 0, transition: "opacity 0.15s", flexShrink: 0 }}>
                  {doneCount > 0 && (
                    <Tooltip title={t("reset")}>
                      <IconButton size="small" sx={PANEL_ROW_ICON_SX} onClick={(e) => { e.stopPropagation(); handleResetWorkflow(workflow.id); }}>
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("edit")}>
                    <IconButton size="small" sx={PANEL_ROW_ICON_SX} onClick={(e) => { e.stopPropagation(); setEditing({ id: workflow.id, name: workflow.name }); }}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {/* 아직 공유 안 한 것만 여기 — 공유 중이면 위 제목 줄에 늘 떠 있다(두 번 내지 않는다). */}
                  {!workflow.share && (
                    <Tooltip title={t("shareWorkflow")}>
                      <IconButton size="small" sx={PANEL_ROW_ICON_SX} onClick={(e) => { e.stopPropagation(); openShare(workflow); }}>
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
              {/* 삭제 모드일 때만, 그것도 접기 화살표 **자리에** 낸다(둘이 나란히 서면 다시 헷갈린다). */}
              {deleteMode ? (
                <Tooltip title={t("delete")}>
                  <IconButton size="small" sx={{ ...PANEL_ROW_ICON_SX, color: PANEL.danger, "&:hover": { color: PANEL.danger, bgcolor: PANEL.dangerWash } }}
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteWf(workflow); }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 18, color: PANEL.iconIdle, flexShrink: 0, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              )}
            </>
          )}
        </Box>
        <Box sx={{ px: "13px", pb: isExpanded ? 0 : "11px" }}>
          <LinearProgress variant="determinate" value={percent}
            sx={{
              height: 4, borderRadius: 2, bgcolor: PANEL.line,
              "& .MuiLinearProgress-bar": { bgcolor: complete ? PANEL.accent : PANEL.accentLineHover },
            }} />
        </Box>
        {/* 카드 본문 */}
        <Collapse in={isExpanded} timeout={200}>
          <Box sx={{ px: "13px", pt: "11px", pb: "13px" }}>
            {/*
              * 단계 목록만 굴린다 — 아래 "단계 추가" 우물은 스크롤 밖에 남겨 늘 손이 닿게 한다
              * (SidePanel 의 toolbar 가 검색창을 본문 스크롤 밖에 두는 것과 같은 이유다).
              * 막대는 가늘게 둔다 — 패널 자신의 스크롤바와 나란히 서므로, 카드 안쪽 것이
              *   더 눈에 띄면 어느 것이 무엇을 굴리는지 헷갈린다(앱의 다른 내부 스크롤 영역과 같은 6px).
              * pr 은 막대와 글자가 붙지 않게 두는 자리다.
              */}
            <Box ref={(node) => { stepListRefs.current[workflow.id] = node; }} sx={{
              /* 안 끌었으면 상한만(짧은 목록은 짧은 대로), 끌었으면 그 높이 그대로. */
              ...(userStepListHeight == null
                ? { maxHeight: STEP_LIST_MAX_HEIGHT }
                : { height: userStepListHeight }),
              overflowY: "auto",
              pr: "4px",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { bgcolor: PANEL.line, borderRadius: 3 },
            }}>
              {workflow.steps.map((step, idx) => WorkflowStep(workflow, step, idx === workflow.steps.length - 1))}
            </Box>

            {/*
              * 높이 조절 손잡이 — 목록 바로 아래(= 목록의 아래 경계). 단계가 없으면 조절할 목록도 없으니 내지 않는다.
              *
              * 카드 폭 전체가 잡는 자리다. 처음엔 패널 폭 손잡이처럼 "평소엔 안 보이고 hover 에서만" 두었는데,
              * 그건 패널 **가장자리**라 거기 뭔가 있으리라 짐작이 되는 자리였다. 여기는 목록과 입력칸 사이라
              * 옅은 선 하나는 손잡이가 아니라 그냥 구분선으로 읽힌다 — 끌 수 있다는 걸 알 방법이 없다.
              * 그래서 평소에도 잡이를 보이게 두되(카드 배경이 accentWash 라 line 색은 묻힌다 → accentLineHover),
              * hover 에서 띠가 들어와 "여기가 손잡이"라고 분명히 답하게 한다.
              */}
            {workflow.steps.length > 0 && (
              <Box onMouseDown={startStepListResize(workflow.id)} title={t("dragToResizeHeightTooltip")}
                sx={{
                  height: 16, mt: "2px", borderRadius: "8px", cursor: "row-resize",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background .15s ease",
                  "&:hover": { bgcolor: PANEL.accentSoft },
                  "&:hover .wf-grip, &:active .wf-grip": { bgcolor: PANEL.accent },
                }}>
                <Box className="wf-grip" sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: PANEL.accentLineHover, transition: "background .15s ease" }} />
              </Box>
            )}
            {/* 단계 추가 영역 — NotesModal 의 작성 카드와 같은 우물(well) */}
            <Box sx={{ mt: workflow.steps.length > 0 ? "12px" : 0, p: "10px", borderRadius: "12px", bgcolor: PANEL.wellBg, boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`, display: "flex", flexDirection: "column", gap: 0.75 }}>
              <TextField size="small" placeholder={t("stepContent")} value={draft.text}
                onChange={(e) => setDrafts({ ...drafts, [workflow.id]: { ...draft, text: e.target.value } })}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddStep(workflow.id); }}
                sx={PANEL_INPUT_SX} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PageAutocomplete options={pageOptions} groupIcons={groupIcons} placeholder={t("selectPage")}
                  value={draft.page} onChange={(_e, page) => setDrafts({ ...drafts, [workflow.id]: { ...draft, page } })} />
                <Tooltip title={t("add")}>
                  <span>
                    <IconButton size="small" disabled={!draft.text?.trim()} onClick={() => handleAddStep(workflow.id)}
                      sx={PANEL_CONFIRM_ICON_SX}>
                      <AddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  /*
   * 공유받은 워크플로우 카드 — 내 카드와 같은 룩이되 **읽기 전용**이다.
   * 단계 정의는 소유자 것을 그대로 비추고(구독), 체크만 내 것이다.
   * 그래서 여기에는 추가·수정·삭제·드래그가 없다. 있는 건 체크와 초기화, 그리고 페이지 링크뿐이다.
   */
  const SharedWorkflowItem = (group, workflow) => {
    const key = sharedKey(group.ownerId, workflow.id);
    const done = sharedProgress[key] ?? {};
    const steps = workflow.steps ?? [];
    const total = steps.length;
    const doneCount = steps.filter((step) => done[step.id]).length;
    const percent = total === 0 ? 0 : Math.round((doneCount / total) * 100);
    const complete = total > 0 && doneCount === total;
    // 펼침 상태는 내 워크플로우와 같은 맵을 쓰되 접두사로 갈라 둔다 — id 가 겹칠 수 있다.
    const cardKey = `shared:${key}`;
    const isExpanded = !!expanded[cardKey];

    return (
      <Box key={cardKey}
        sx={{
          mb: "9px", borderRadius: "12px",
          bgcolor: isExpanded ? PANEL.accentWash : "#fff",
          boxShadow: `inset 0 0 0 1px ${isExpanded ? PANEL.accentLine : PANEL.line}`,
          overflow: "hidden", transition: "box-shadow .15s ease, background .15s ease",
          "&:hover": { boxShadow: `inset 0 0 0 1px ${PANEL.accentLine}, 0 4px 12px rgba(11,60,92,.08)` },
          "&:hover .wf-actions": { opacity: 1 },
        }}>
        <Box onClick={() => setExpanded({ ...expanded, [cardKey]: !isExpanded })}
          sx={{ display: "flex", alignItems: "center", gap: 0.75, px: "13px", pt: "11px", pb: "9px", cursor: "pointer" }}>
          {/* 공유자 — 이름을 적어 둔다. 누가 준 절차인지가 이 카드의 첫 번째 정보다. */}
          <Tooltip title={`${t("sharedOwner")}: ${group.ownerName}`}>
            <PersonOutlineIcon sx={{ fontSize: 15, color: PANEL.iconIdle, flexShrink: 0 }} />
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ fontSize: FS.TITLE, fontWeight: 500, color: PANEL.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {workflow.name}
            </Box>
            <Box sx={{ fontSize: FS.CAPTION, color: PANEL.inkFaint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {group.ownerName}
            </Box>
          </Box>
          {complete ? (
            <PanelBadge sx={{ lineHeight: 1.5 }}>100%</PanelBadge>
          ) : (
            <Box sx={{
              flex: "none", fontSize: FS.CAPTION, fontWeight: 500, lineHeight: 1.5,
              color: PANEL.inkMuted, bgcolor: PANEL.fieldBg, borderRadius: "9px", px: "9px", py: "2px",
            }}>
              {`${doneCount}/${total}`}
            </Box>
          )}
          {doneCount > 0 && (
            <Box className="wf-actions" sx={{ display: "flex", opacity: 0, transition: "opacity 0.15s", flexShrink: 0 }}>
              <Tooltip title={t("reset")}>
                <IconButton size="small" sx={PANEL_ROW_ICON_SX}
                  onClick={(e) => { e.stopPropagation(); handleResetSharedProgress(group.ownerId, workflow.id); }}>
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <ExpandMoreIcon sx={{ fontSize: 18, color: PANEL.iconIdle, flexShrink: 0, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </Box>
        <Box sx={{ px: "13px", pb: isExpanded ? 0 : "11px" }}>
          <LinearProgress variant="determinate" value={percent}
            sx={{
              height: 4, borderRadius: 2, bgcolor: PANEL.line,
              "& .MuiLinearProgress-bar": { bgcolor: complete ? PANEL.accent : PANEL.accentLineHover },
            }} />
        </Box>
        <Collapse in={isExpanded} timeout={200}>
          <Box sx={{ px: "13px", pt: "11px", pb: "13px", maxHeight: STEP_LIST_MAX_HEIGHT, overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: PANEL.line, borderRadius: 3 },
          }}>
            {steps.map((step, idx) => {
              // 내 계정에 권한이 없는 페이지는 링크를 잠근다 — 내 워크플로우 단계와 같은 규칙이다.
              const page = step.menuKey != null ? permittedPageMap.get(step.menuKey) : null;
              const isDone = !!done[step.id];
              const isLast = idx === steps.length - 1;
              return (
                <Box key={step.id} sx={{ display: "flex", gap: 0.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 24, flexShrink: 0 }}>
                    <Checkbox size="small" checked={isDone}
                      onChange={() => handleToggleSharedStep(group.ownerId, workflow.id, step.id)}
                      icon={<RadioButtonUncheckedIcon sx={{ fontSize: 18, color: PANEL.iconFaint }} />}
                      checkedIcon={<CheckCircleIcon sx={{ fontSize: 18, color: PANEL.accent }} />}
                      sx={{ p: 0.25 }} />
                    {!isLast && <Box sx={{ flex: 1, width: "2px", bgcolor: isDone ? PANEL.accentLine : PANEL.line, borderRadius: 1, my: 0.25 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 1.25 }}>
                    <Box sx={{ fontSize: FS.TITLE, lineHeight: 1.5, wordBreak: "break-word", textDecoration: isDone ? "line-through" : "none", color: isDone ? PANEL.inkFaint : PANEL.inkBody }}>
                      {step.text}
                    </Box>
                    {step.menuKey != null && (
                      <Chip size="small" clickable={!!page} disabled={!page}
                        icon={<LaunchIcon sx={{ fontSize: "13px !important" }} />}
                        label={page?.label ?? "-"}
                        onClick={page ? () => openMenuTab(step.menuKey) : undefined}
                        sx={{
                          mt: 0.5, height: 22, maxWidth: "100%", fontWeight: 500,
                          bgcolor: PANEL.accentWash,
                          color: PANEL.accentDeep,
                          boxShadow: `inset 0 0 0 1px ${PANEL.accentLine}`,
                          "& .MuiChip-icon": { color: PANEL.accentDeep },
                          "&:hover": { bgcolor: PANEL.accentSoft },
                          "&.Mui-disabled": { opacity: 1, bgcolor: PANEL.fieldBg, color: PANEL.inkFaint, boxShadow: "none", "& .MuiChip-icon": { color: PANEL.iconFaint } },
                        }} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const sharedCount = sharedGroups.reduce((n, group) => n + (group.workflows?.length ?? 0), 0);

  /*
   * 헤더 부제 — **워크플로우 단위**로 센다. 다른 패널(LayoutListModal 의 "Layout 5")과 같은 문법이다.
   *
   * 예전에는 모든 워크플로우의 단계를 통째로 더해 "Step 0/13" 처럼 냈다. 워크플로우는 서로 다른 절차라
   * 그 합계는 뜻하는 바가 없고, 카드에는 0/11 · 0/2 만 보이는데 헤더에만 13 이 나오니
   * 어디서 온 수인지 알 수 없어 "값이 틀렸다"로 읽혔다.
   * 헤더는 목록 전체를 말하는 자리이므로 목록의 단위, 즉 워크플로우 몇 개 중 몇 개를 끝냈는지로 센다.
   * (단계가 하나도 없는 워크플로우는 "끝냈다"고 할 수 없으므로 완료로 세지 않는다 — 카드 배지와 같은 기준.)
   */
  const workflowTotal = workflows.length;
  const workflowDone = workflows.filter((wf) => wf.steps.length > 0 && wf.steps.every((s) => s.done)).length;

  return (
    /*
     * 셸은 공용 SidePanel 을 쓰되 **도킹 패널**로 눌러 놓는다 — 이 패널은 Dialog 위에 뜨는
     * 카드가 아니라 본문 옆에 붙어 화면 끝까지 닿고 폭도 드래그로 바뀐다.
     * 그래서 radius/그림자만 끄고(둥근 카드가 화면 모서리에서 잘려 보인다) 헤더·본문 규격은 그대로 받는다.
     * 폭 조절 핸들은 SidePanel 이 `overflow: hidden` 이라 바깥 래퍼에 둔다.
     */
    <Box sx={{ position: "relative", flexShrink: 0, display: "flex", minHeight: 0 }}>
      {/* 좌측 너비 조절 핸들 — 드래그로 패널 폭 조정 */}
      <Box onMouseDown={startResize} title={t("dragToResizeWidthTooltip")}
        sx={{
          position: "absolute", left: 0, top: 0, width: 6, height: "100%",
          cursor: "col-resize", zIndex: 5,
          "&:hover": { bgcolor: PANEL.accentLine },
          "&:active": { bgcolor: PANEL.accent },
        }} />
      <SidePanel
        id="workflow-panel"
        icon={<ChecklistIcon />}
        title={t("workflow")}
        /* 라벨은 붙이지 않는다 — 바로 위 제목이 이미 "Workflow" 라 "Workflow / Workflow 0/2" 가 된다.
           카드 배지(0/11)와 같은 문법이라 제목 아래 숫자만으로 읽힌다. */
        subtitle={workflowTotal > 0 ? `${workflowDone}/${workflowTotal}` : null}
        onClose={() => dispatch(setWorkflowPanelOpen(false))}
        closeLabel={t("close")}
        width={panelWidth}
        actions={
          <>
            {/*
              * 삭제 모드 토글 — 추가(+)보다 왼쪽에 둔다. 오른쪽 끝은 패널 닫기(×)라,
              * 지우는 버튼을 그 옆에 두면 카드에서 겪던 "옆 버튼 잘못 누르기"를 헤더에서 되풀이하게 된다.
              *
              * 켜진 표시는 + 토글과 같은 규격으로 적어 둔다. 다만 실제로 눈에 드는 건 **아이콘 색**뿐이고
              * 알약 배경(bgcolor)은 이 헤더에서 먹지 않는다 — + 토글도 마찬가지다(전역 스타일이 sx 의
              * background 를 이긴다. CLAUDE.md 의 "scss beats sx" 참고). 지금은 모드가 켜지면 카드마다
              * 접기 화살표가 빨간 휴지통으로 바뀌므로 상태는 그쪽에서 분명히 읽힌다.
              */}
            <Tooltip title={t("deleteWorkflowModeTooltip")}>
              <IconButton size="small" onClick={() => setDeleteMode(!deleteMode)}
                sx={{
                  ...PANEL_HEAD_ICON_SX,
                  ...(deleteMode && {
                    color: PANEL.danger,
                    bgcolor: PANEL.dangerWash,
                    "&:hover": { color: PANEL.danger, bgcolor: PANEL.dangerWash },
                  }),
                }}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("add")}>
            <IconButton size="small" onClick={() => setAdding(!adding)}
              /*
               * 켜져 있으면 알약으로 눌러 둔다. 예전처럼 +를 45° 돌려 ×로 만들면
               * 바로 옆 패널 닫기(×)와 구분이 안 된다 — 같은 줄에 ×가 둘이 되는 셈이다.
               */
              sx={{
                ...PANEL_HEAD_ICON_SX,
                ...(adding && {
                  color: PANEL.accentHover,
                  bgcolor: PANEL.accentSoft,
                  "&:hover": { color: PANEL.accentDeep, bgcolor: PANEL.accentSoft },
                }),
              }}>
              <AddIcon />
            </IconButton>
            </Tooltip>
          </>
        }
        /* 새 워크플로우 이름 입력 — 헤더 바로 아래 고정 줄(목록이 길어져도 밀려 올라가지 않는다) */
        toolbar={adding ? (
          <>
            <TextField size="small" autoFocus placeholder={t("workflowName")} value={newName}
              sx={{ flex: 1, ...PANEL_INPUT_SX }}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddWorkflow(); }} />
            <Tooltip title={t("add")}>
              <span>
                <IconButton size="small" disabled={!newName.trim()} onClick={handleAddWorkflow}
                  sx={PANEL_CONFIRM_ICON_SX}>
                  <DoneIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : null}
        sx={{
          height: "100%",
          borderRadius: 0,
          boxShadow: "none",
          borderLeft: `1px solid ${PANEL.headLine}`,
        }}
        bodySx={{ gap: 0 }}>
        {/*
          * 불러오는 중에는 로딩만 낸다. 빈 화면(MSG_NoWorkflow)은 "정말 없다"는 말이라
          * 아직 모르는 동안 그걸 내보내면 안 된다 — AgentChatPanel 의 대화 목록과 같은 순서
          * (로딩 → 빈 화면 → 목록)로 맞춘다.
          * 자리는 빈 화면의 아이콘과 같은 pt 에 둔다. 로딩이 끝나며 세로로 튀지 않게.
          */}
        {loading ? (
          /* 아직 모르는 동안은 스피너 아니면 **아무것도** 낸다 — 200ms 안쪽의 빈 본문은
             눈에 들지 않지만, 여기에 빈 화면을 내면 원래 고치려던 그 오해가 그대로 돌아온다. */
          showSpinner ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
              <CircularProgress size={28} sx={{ color: PANEL.accentHover }} />
            </Box>
          ) : null
        ) : /* 빈 화면은 **내 것도 공유받은 것도 없을 때**만 낸다 — 공유받은 카드가 아래 떠 있는데
              "등록된 워크플로우가 없습니다"가 같이 보이면 서로 맞지 않는 말이 된다. */
        workflows.length === 0 && sharedCount === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", pt: 6 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: PANEL.accentSoft }}>
              <ChecklistIcon sx={{ fontSize: 28, color: PANEL.accentHover }} />
            </Box>
            <Box sx={{ fontSize: FS.TITLE, color: PANEL.inkMuted, textAlign: "center", px: 3 }}>{t("MSG_NoWorkflow")}</Box>
          </Box>
        ) : (
          // 즐겨찾기 우선 표시 — 안정 정렬이라 각 그룹(즐겨찾기/일반) 내 드래그 순서는 유지
          [...workflows]
            .sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0))
            .map((workflow) => WorkflowItem(workflow))
        )}

        {/*
          * 공유받은 것은 내 목록 **아래**에 따로 묶는다. 섞어 놓으면 고칠 수 있는 카드와
          * 볼 수만 있는 카드가 한 줄씩 번갈아 나와, 어느 것이 내 것인지 카드마다 다시 확인해야 한다.
          * 삭제 모드에서도 이 구역은 그대로다 — 남의 워크플로우는 애초에 여기서 지울 수 없다.
          */}
        {sharedCount > 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: workflows.length > 0 ? "14px" : 0, mb: "8px", px: "2px" }}>
              <PeopleAltOutlinedIcon sx={{ fontSize: 15, color: PANEL.iconIdle }} />
              <Box sx={{ fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: 0.4, color: PANEL.inkMuted }}>
                {t("sharedWorkflows")}
              </Box>
              <Box sx={{ fontSize: FS.CAPTION, color: PANEL.inkFaint }}>{sharedCount}</Box>
            </Box>
            {sharedGroups.map((group) => (group.workflows ?? []).map((workflow) => SharedWorkflowItem(group, workflow)))}
          </>
        )}
      </SidePanel>

      {/*
        * 공유 설정 — 공용 Dialog(components/Dialog) 를 쓰지 않는다. 그 다이얼로그는 확인 버튼을 잠글 수
        * 없어서, "직책 공유인데 직책을 하나도 안 골랐다"를 막을 방법이 확인 뒤 조용히 무시하는 것뿐이다.
        * 겉모습(모서리·제목 규격·구분선·우측 정렬 액션)은 그대로 맞춘다.
        */}
      <MuiDialog open={sharing != null} onClose={() => setSharing(null)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontSize: FS.DISPLAY, fontWeight: 700, pl: 2, pb: 1 }}>{t("shareWorkflow")}</DialogTitle>
        <ModalRule />
        <DialogContent sx={{ px: 2, py: 2 }}>
          <Box sx={{ fontSize: FS.TITLE, fontWeight: 600, color: PANEL.ink, mb: 0.5 }}>{sharing?.name}</Box>
          <RadioGroup value={sharing?.scope ?? SHARE_SCOPE_ALL}
            onChange={(e) => setSharing({ ...sharing, scope: e.target.value })}>
            <FormControlLabel value={SHARE_SCOPE_ALL} control={<Radio size="small" />}
              label={<Box sx={{ fontSize: FS.TITLE }}>{t("shareScopeAll")}</Box>} />
            <FormControlLabel value={SHARE_SCOPE_POSTN} control={<Radio size="small" />}
              label={<Box sx={{ fontSize: FS.TITLE }}>{t("shareScopePostn")}</Box>} />
          </RadioGroup>
          {sharing?.scope === SHARE_SCOPE_POSTN && (
            <Box sx={{ mt: 1 }}>
              <AppAutocomplete multiple disableCloseOnSelect options={postnOptions}
                placeholder={t("sharePostnPlaceholder")}
                value={postnOptions.filter((option) => (sharing.postnCds ?? []).includes(option.key))}
                onChange={(_e, selected) => setSharing({ ...sharing, postnCds: selected.map((option) => option.key) })} />
              {(sharing.postnCds ?? []).length === 0 && (
                <Box sx={{ mt: 0.75, fontSize: FS.BODY, color: "error.main" }}>{t("MSG_SelectPostnToShare")}</Box>
              )}
            </Box>
          )}
        </DialogContent>
        <ModalRule />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          {/* 이미 공유 중일 때만 해제를 낸다 — 공유한 적 없는 카드에 "공유 해제"가 있으면 상태를 잘못 읽게 된다. */}
          {workflows.find((wf) => wf.id === sharing?.id)?.share && (
            <Button size="small" color="error" startIcon={<CloseIcon />} sx={{ mr: "auto" }}
              onClick={() => handleUnshare(sharing.id)}>{t("unshare")}</Button>
          )}
          <Button size="small" startIcon={<CloseIcon />} onClick={() => setSharing(null)}>{t("cancel")}</Button>
          <Button size="small" variant="contained" disableElevation startIcon={<DoneAllIcon />} sx={{ minWidth: 72 }}
            disabled={sharing?.scope === SHARE_SCOPE_POSTN && (sharing?.postnCds ?? []).length === 0}
            onClick={handleSaveShare}>{t("ok")}</Button>
        </DialogActions>
      </MuiDialog>

      {/*
        * 삭제 확인 — 워크플로우를 지우면 단계까지 통째로 사라지고 되돌릴 방법이 없다.
        * 무엇을 지우는지 이름을 적어 준다("정말 지울까요?"만으로는 어느 카드인지 알 수 없다).
        */}
      <Dialog
        open={confirmDeleteWf != null}
        onClose={() => setConfirmDeleteWf(null)}
        title="delete"
        content={`${confirmDeleteWf?.name ?? ""}\n${t("MSG_DeleteConfirm")}`}
        onHandler={(action) => {
          if (action === "DELETE" && confirmDeleteWf) {
            handleDeleteWorkflow(confirmDeleteWf.id);
          }
        }}
      />
    </Box>
  );
};

// Navbar 버튼으로 토글되는 우측 개인 워크플로우 패널 — AgentChatPanel과 동일한 인라인 flex 배치 패턴.
const WorkflowPanel = () => {
  const open = useSelector(getWorkflowPanelOpen);
  if (!open) return null;
  return <WorkflowPanelBody />;
};

export default WorkflowPanel;
